import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Fab,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Container,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  LocalHospital as MedicalIcon,
  AccountBalance as BankIcon,
  ContactPhone as ContactIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { emergencyService } from '../services/emergencyService';
import { EmergencyInfo, PhoneNumber, Address, EmergencyContact } from '../types';
import PasswordManager from '../utils/passwordManager';
import PrimaryButton from '../components/PrimaryButton';

const EmergencyInfoForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados para mostrar formularios de añadir nuevos items
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  
  // Estados para loading individual de cada sección
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  
  // Estados para feedback individual
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const [addressSuccess, setAddressSuccess] = useState('');
  const [contactSuccess, setContactSuccess] = useState('');
  
  const [data, setData] = useState<EmergencyInfo>({
    phone_numbers: [],
    addresses: [],
    medical_info: {
      blood_type: '',
      allergies: [],
      medications: [],
      conditions: [],
      emergency_notes: '',
    },
    financial_info: {
      bank_name: '',
      account_type: '',
      account_number: '',
      additional_info: '',
    },
    emergency_contacts: [],
    additional_notes: '',
  });

  // Estados para nuevos items
  const [newPhone, setNewPhone] = useState<PhoneNumber>({ name: '', number: '', relationship: '' });
  const [newAddress, setNewAddress] = useState<Address>({ type: '', street: '', city: '', region: '', postal_code: '' });
  const [newContact, setNewContact] = useState<EmergencyContact>({ name: '', phone: '', relationship: '', email: '' });
  const [newAllergy, setNewAllergy] = useState('');
  const [newMedication, setNewMedication] = useState('');
  const [newCondition, setNewCondition] = useState('');

  // Estados para formularios individuales
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [phoneFormOpen, setPhoneFormOpen] = useState(false);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  
  // Estados para feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    // Verificar si tenemos contraseña válida
    const savedPassword = PasswordManager.getTemporaryPassword();
    if (savedPassword) {
      loadEmergencyInfo(savedPassword);
    } else {
      setPasswordDialog(true);
    }
  }, []);

  const loadEmergencyInfo = async (pwd: string) => {
    setLoading(true);
    try {
      const emergencyInfo = await emergencyService.getEmergencyInfo(pwd);
      setData(emergencyInfo);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No existe información, es primera vez - esto está bien
      } else {
        setError('Error al cargar la información');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      setError(t('emergencyForm.password.required'));
      return;
    }

    try {
    setLoading(true);
    setError('');

      // Cargar información existente
      const info = await emergencyService.getEmergencyInfo(password);
      setData(info);
      
      setPasswordDialog(false);
      PasswordManager.setTemporaryPassword(password, 600000); // 10 minutos
      
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No hay información guardada, continuar
        setData({
          phone_numbers: [],
          addresses: [],
          medical_info: {
            blood_type: '',
            allergies: [],
            medications: [],
            conditions: [],
            emergency_notes: '',
          },
          financial_info: {
            bank_name: '',
            account_type: '',
            account_number: '',
            additional_info: '',
          },
          emergency_contacts: [],
          additional_notes: '',
        });
        setPasswordDialog(false);
        PasswordManager.setTemporaryPassword(password, 600000);
      } else {
        setError(err.response?.data?.detail || t('emergencyForm.actions.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const savedPassword = PasswordManager.getTemporaryPassword();
      if (!savedPassword) {
        setError('Sesión expirada. Por favor, ingresa tu contraseña nuevamente.');
        setPasswordDialog(true);
        return;
      }

      if (data.id) {
        await emergencyService.updateEmergencyInfo(data, savedPassword);
      } else {
        await emergencyService.createEmergencyInfo(data, savedPassword);
      }
      setSuccess('Información guardada exitosamente');
      
      // Extender sesión después de guardar exitosamente
      PasswordManager.extendSession(600000);
    } catch (err: any) {
      setError('Error al guardar la información');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContact = async () => {
    if (!newContact.name || !newContact.phone || !newContact.relationship) return;
    
    try {
      setLoading(true);
      const contactData = {
        name: newContact.name,
        phone: newContact.phone,
        relationship: newContact.relationship,
        email: newContact.email || '',
      };
      
      // Actualizar lista local
      const updatedContacts = [...(data.emergency_contacts || []), contactData];
      const updatedData = {...data, emergency_contacts: updatedContacts};
      
      const savedPassword = PasswordManager.getTemporaryPassword();
      if (!savedPassword) {
        setError(t('emergencyForm.actions.sessionExpired'));
        setPasswordDialog(true);
        return;
      }

      if (data.id) {
        await emergencyService.updateEmergencyInfo(updatedData, savedPassword);
      } else {
        const newData = await emergencyService.createEmergencyInfo(updatedData, savedPassword);
        setData(newData);
      }
      
      setData(updatedData);
      
      // Limpiar formulario
      setNewContact({ name: '', phone: '', relationship: '', email: '' });
      setContactFormOpen(false);
      
      // Mostrar éxito
      setSnackbar({
        open: true,
        message: t('emergencyForm.contacts.saved'),
        severity: 'success'
      });
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('emergencyForm.actions.saveError'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePhone = async () => {
    if (!newPhone.name || !newPhone.number) return;
    
    try {
      setLoading(true);
      const phoneData = {
        name: newPhone.name,
        number: newPhone.number,
        relationship: newPhone.relationship || '',
      };
      
      // Actualizar lista local
      const updatedPhones = [...(data.phone_numbers || []), phoneData];
      const updatedData = {...data, phone_numbers: updatedPhones};
      
      const savedPassword = PasswordManager.getTemporaryPassword();
      if (!savedPassword) {
        setError(t('emergencyForm.actions.sessionExpired'));
        setPasswordDialog(true);
        return;
      }

      if (data.id) {
        await emergencyService.updateEmergencyInfo(updatedData, savedPassword);
      } else {
        const newData = await emergencyService.createEmergencyInfo(updatedData, savedPassword);
        setData(newData);
      }
      
      setData(updatedData);
      
      // Limpiar formulario
      setNewPhone({ name: '', number: '', relationship: '' });
      setPhoneFormOpen(false);
      
      // Mostrar éxito
      setSnackbar({
        open: true,
        message: t('emergencyForm.phones.saved'),
        severity: 'success'
      });
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('emergencyForm.actions.saveError'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!newAddress.type || !newAddress.street || !newAddress.city) return;
    
    try {
      setLoading(true);
      const addressData = {
        type: newAddress.type,
        street: newAddress.street,
        city: newAddress.city,
        region: newAddress.region || '',
        postal_code: newAddress.postal_code || '',
      };
      
      // Actualizar lista local
      const updatedAddresses = [...(data.addresses || []), addressData];
      const updatedData = {...data, addresses: updatedAddresses};
      
      const savedPassword = PasswordManager.getTemporaryPassword();
      if (!savedPassword) {
        setError(t('emergencyForm.actions.sessionExpired'));
        setPasswordDialog(true);
        return;
      }

      if (data.id) {
        await emergencyService.updateEmergencyInfo(updatedData, savedPassword);
      } else {
        const newData = await emergencyService.createEmergencyInfo(updatedData, savedPassword);
        setData(newData);
      }
      
      setData(updatedData);
      
      // Limpiar formulario
      setNewAddress({ type: '', street: '', city: '', region: '', postal_code: '' });
      setAddressFormOpen(false);
      
      // Mostrar éxito
      setSnackbar({
        open: true,
        message: t('emergencyForm.addresses.saved'),
        severity: 'success'
      });
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('emergencyForm.actions.saveError'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setLoading(true);
      
      const dataToSave = {
        medical_info: {
          blood_type: data.medical_info?.blood_type || '',
          emergency_notes: data.medical_info?.emergency_notes || '',
          allergies: data.medical_info?.allergies || [],
          medications: data.medical_info?.medications || [],
          conditions: data.medical_info?.conditions || [],
        },
        financial_info: {
          bank_name: data.financial_info?.bank_name || '',
          account_type: data.financial_info?.account_type || '',
          account_number: data.financial_info?.account_number || '',
          additional_info: data.financial_info?.additional_info || '',
        },
        additional_notes: data.additional_notes || '',
        phone_numbers: data.phone_numbers || [],
        addresses: data.addresses || [],
        emergency_contacts: data.emergency_contacts || [],
      };
      
      const savedPassword = PasswordManager.getTemporaryPassword();
      if (!savedPassword) {
        setError(t('emergencyForm.actions.sessionExpired'));
        setPasswordDialog(true);
        return;
      }

      if (data.id) {
        await emergencyService.updateEmergencyInfo(dataToSave, savedPassword);
      } else {
        await emergencyService.createEmergencyInfo(dataToSave, savedPassword);
      }
      
      setSnackbar({
        open: true,
        message: t('emergencyForm.actions.success'),
        severity: 'success'
      });
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('emergencyForm.actions.saveError'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Funciones para eliminar items
  const removePhoneNumber = (index: number) => {
    setData({
      ...data,
      phone_numbers: data.phone_numbers?.filter((_, i) => i !== index),
    });
  };

  const removeAddress = (index: number) => {
    setData({
      ...data,
      addresses: data.addresses?.filter((_, i) => i !== index),
    });
  };

  const removeContact = (index: number) => {
    setData({
      ...data,
      emergency_contacts: data.emergency_contacts?.filter((_, i) => i !== index),
    });
  };

  // Componente para mostrar datos existentes de forma llamativa
  const renderExistingData = (items: any[], type: 'phone' | 'address' | 'contact', removeFunction: (index: number) => void) => {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {items.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card sx={{ 
              border: 1, 
              borderColor: 'primary.main',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'primary.50',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'primary.100',
                transition: 'background-color 0.2s'
              }
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {type === 'phone' && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      📞 {item.number}
                    </Typography>
                    {item.relationship && (
                      <Typography variant="caption" color="text.secondary">
                        {item.relationship}
                      </Typography>
                    )}
                  </>
                )}
                {type === 'address' && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {item.type}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      📍 {item.street}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.city}
                    </Typography>
                  </>
                )}
                {type === 'contact' && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      👥 {item.relationship}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      📞 {item.phone}
                    </Typography>
                    {item.email && (
                      <Typography variant="caption" color="text.secondary">
                        ✉️ {item.email}
                      </Typography>
                    )}
                  </>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => removeFunction(index)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (passwordDialog) {
    return (
      <Dialog open={passwordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LockIcon color="primary" />
            {t('emergencyForm.password.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('emergencyForm.password.description')}
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label={t('emergencyForm.password.placeholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/dashboard')}>{t('emergencyForm.password.cancel')}</Button>
          <Button 
            onClick={handlePasswordSubmit} 
            variant="contained" 
            disabled={loading || !password}
          >
            {loading ? <CircularProgress size={24} /> : t('emergencyForm.password.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
    <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {t('emergencyForm.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
              {t('emergencyForm.subtitle')}
        </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* SECCIÓN 1: CONTACTOS DE EMERGENCIA */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('emergencyForm.contacts.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('emergencyForm.contacts.description')}
                </Typography>
                
                {/* Mostrar contactos existentes */}
                {data.emergency_contacts && data.emergency_contacts.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {data.emergency_contacts.map((contact, index) => (
                      <Chip
                        key={index}
                        label={`${contact.name} (${contact.relationship}) - ${contact.phone}`}
                        variant="outlined"
                        sx={{ mr: 1, mb: 1 }}
              />
                    ))}
                  </Box>
                )}
                
                <PrimaryButton
                  startIcon={<AddIcon />}
                  onClick={() => setContactFormOpen(true)}
                  variant="outlined"
                >
                  {data.emergency_contacts && data.emergency_contacts.length > 0
                    ? t('emergencyForm.contacts.addNew')
                    : t('emergencyForm.contacts.add')
                  }
                </PrimaryButton>
              </CardContent>
            </Card>
          </Grid>
          
          {/* SECCIÓN 2: NÚMEROS TELEFÓNICOS */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('emergencyForm.phones.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('emergencyForm.phones.description')}
                </Typography>
                
                {/* Mostrar teléfonos existentes */}
                {data.phone_numbers && data.phone_numbers.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {data.phone_numbers.map((phone, index) => (
                      <Chip
                        key={index}
                        label={`${phone.name}: ${phone.number}`}
                        variant="outlined"
                        sx={{ mr: 1, mb: 1 }}
              />
                    ))}
                  </Box>
                )}
                
                <PrimaryButton
                  startIcon={<AddIcon />}
                  onClick={() => setPhoneFormOpen(true)}
                  variant="outlined"
                >
                  {data.phone_numbers && data.phone_numbers.length > 0
                    ? t('emergencyForm.phones.addNew')
                    : t('emergencyForm.phones.add')
                  }
                </PrimaryButton>
              </CardContent>
            </Card>
          </Grid>
          
          {/* SECCIÓN 3: DIRECCIONES */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HomeIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('emergencyForm.addresses.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('emergencyForm.addresses.description')}
                </Typography>

                {/* Mostrar direcciones existentes */}
                {data.addresses && data.addresses.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {data.addresses.map((address, index) => (
                      <Chip
                        key={index}
                        label={`${address.type}: ${address.street}, ${address.city}`}
                        variant="outlined"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
                
                <PrimaryButton
                  startIcon={<AddIcon />}
                  onClick={() => setAddressFormOpen(true)}
                  variant="outlined"
                >
                  {data.addresses && data.addresses.length > 0
                    ? t('emergencyForm.addresses.addNew')
                    : t('emergencyForm.addresses.add')
                  }
                </PrimaryButton>
              </CardContent>
            </Card>
          </Grid>

          {/* SECCIÓN 4: INFORMACIÓN MÉDICA */}
            <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MedicalIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('emergencyForm.medical.title')}
                  </Typography>
                </Box>
              <TextField
                fullWidth
                  label={t('emergencyForm.medical.bloodType')}
                value={data.medical_info?.blood_type || ''}
                onChange={(e) => setData({
                  ...data,
                  medical_info: { ...data.medical_info, blood_type: e.target.value }
                })}
                  sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                  label={t('emergencyForm.medical.notes')}
                multiline
                rows={3}
                value={data.medical_info?.emergency_notes || ''}
                onChange={(e) => setData({
                  ...data,
                  medical_info: { ...data.medical_info, emergency_notes: e.target.value }
                })}
              />
              </CardContent>
            </Card>
          </Grid>

          {/* SECCIÓN 5: INFORMACIÓN FINANCIERA */}
            <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BankIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('emergencyForm.financial.title')}
                  </Typography>
                </Box>
              <TextField
                fullWidth
                  label={t('emergencyForm.financial.bankName')}
                value={data.financial_info?.bank_name || ''}
                onChange={(e) => setData({
                  ...data,
                  financial_info: { ...data.financial_info, bank_name: e.target.value }
                })}
                  sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                  label={t('emergencyForm.financial.accountType')}
                  placeholder={t('emergencyForm.financial.accountPlaceholder')}
                  helperText={t('emergencyForm.financial.accountHelper')}
                value={data.financial_info?.account_type || ''}
                onChange={(e) => setData({
                  ...data,
                  financial_info: { ...data.financial_info, account_type: e.target.value }
                })}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* SECCIÓN 6: NOTAS ADICIONALES */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <NotesIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('emergencyForm.additionalNotes.title')}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={t('emergencyForm.additionalNotes.placeholder')}
                  value={data.additional_notes || ''}
                  onChange={(e) => setData({ ...data, additional_notes: e.target.value })}
                />
              </CardContent>
            </Card>
            </Grid>

          {/* Botón de guardado principal */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <PrimaryButton
                onClick={handleSaveAll}
                disabled={loading}
                size="large"
                sx={{ px: 4, py: 1.5 }}
              >
                {loading ? t('emergencyForm.actions.saving') : t('emergencyForm.actions.save')}
              </PrimaryButton>
            </Box>
          </Grid>
        </Grid>

        {/* Diálogos para formularios individuales */}
        {/* Diálogo de contacto */}
        <Dialog open={contactFormOpen} onClose={() => setContactFormOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('emergencyForm.contacts.add')}</DialogTitle>
          <DialogContent>
              <TextField
                fullWidth
              label={t('emergencyForm.contacts.name')}
              value={newContact.name || ''}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              sx={{ mb: 2, mt: 1 }}
              />
              <TextField
                fullWidth
              label={t('emergencyForm.contacts.phone')}
              value={newContact.phone || ''}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
              sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
              label={t('emergencyForm.contacts.relationship')}
              value={newContact.relationship || ''}
                onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
              sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
              label={t('emergencyForm.contacts.email')}
              value={newContact.email || ''}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <PrimaryButton onClick={handleSaveContact}>
              {t('emergencyForm.contacts.save')}
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        {/* Diálogo de teléfono */}
        <Dialog open={phoneFormOpen} onClose={() => setPhoneFormOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('emergencyForm.phones.add')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('emergencyForm.phones.description_field')}
              placeholder={t('emergencyForm.phones.descriptionPlaceholder')}
              helperText={t('emergencyForm.phones.descriptionHelper')}
              value={newPhone.name || ''}
              onChange={(e) => setNewPhone({ ...newPhone, name: e.target.value })}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label={t('emergencyForm.phones.number')}
              placeholder={t('emergencyForm.phones.numberPlaceholder')}
              value={newPhone.number || ''}
              onChange={(e) => setNewPhone({ ...newPhone, number: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('emergencyForm.phones.notes')}
              placeholder={t('emergencyForm.phones.notesPlaceholder')}
              value={newPhone.relationship || ''}
              onChange={(e) => setNewPhone({ ...newPhone, relationship: e.target.value })}
                />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPhoneFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <PrimaryButton onClick={handleSavePhone}>
              {t('emergencyForm.phones.save')}
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        {/* Diálogo de dirección */}
        <Dialog open={addressFormOpen} onClose={() => setAddressFormOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('emergencyForm.addresses.add')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('emergencyForm.addresses.type')}
              placeholder={t('emergencyForm.addresses.typePlaceholder')}
              value={newAddress.type || ''}
              onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label={t('emergencyForm.addresses.street')}
              value={newAddress.street || ''}
              onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
              sx={{ mb: 2 }}
            />
        <TextField
          fullWidth
              label={t('emergencyForm.addresses.city')}
              value={newAddress.city || ''}
              onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddressFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <PrimaryButton onClick={handleSaveAddress}>
              {t('emergencyForm.addresses.save')}
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar para feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EmergencyInfoForm; 